// Cliente del módulo de Analítica de Redes (Enfoque B: cuentas).
// Pega contra los endpoints /api/social/* del backend ECS.

const API = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Las imágenes del CDN de Instagram bloquean hotlinking y caducan; las servimos
// vía proxy del backend. Devuelve la URL lista para usar como src de <img>.
export const proxyImage = (url) =>
    url ? `${API}/api/social/image-proxy?url=${encodeURIComponent(url)}` : '';

async function jsonFetch(url, options = {}) {
    // no-store: el polling repite el mismo GET; sin esto el navegador puede
    // servir una respuesta cacheada (p. ej. comentarios aún vacíos).
    const res = await fetch(url, { cache: 'no-store', ...options });
    let data = {};
    try { data = await res.json(); } catch (_) { /* sin cuerpo */ }
    if (!res.ok) {
        // El backend manda { error, reason, quota } en 4xx/429.
        const err = new Error(data.error || `HTTP ${res.status}`);
        err.status = res.status;
        err.reason = data.reason;
        err.quota = data.quota;
        throw err;
    }
    return data;
}

const qs = (obj) =>
    Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');

export const socialApi = {
    listAccounts: (username, platform = '') =>
        jsonFetch(`${API}/api/social/accounts?${qs({ username, platform })}`),

    addAccount: (username, platform, accountUsername, accountUrl = '', kind = 'own', trackBy = 'profile') =>
        jsonFetch(`${API}/api/social/accounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                username, platform, kind, track_by: trackBy,
                account_username: accountUsername, account_url: accountUrl,
            }),
        }),

    removeAccount: (username, accountId) =>
        jsonFetch(`${API}/api/social/accounts?${qs({ username, account_id: accountId })}`,
            { method: 'DELETE' }),

    setAccountKind: (username, accountId, kind) =>
        jsonFetch(`${API}/api/social/accounts/kind`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, account_id: accountId, kind }),
        }),

    reorderAccounts: (username, accountIds) =>
        jsonFetch(`${API}/api/social/accounts/reorder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, account_ids: accountIds }),
        }),

    refreshAccount: (username, accountId) =>
        jsonFetch(`${API}/api/social/accounts/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, account_id: accountId }),
        }),

    refreshComments: (username, postPk) =>
        jsonFetch(`${API}/api/social/posts/comments/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, post_pk: postPk }),
        }),

    analyzePost: (username, postPk) =>
        jsonFetch(`${API}/api/social/posts/sentiment/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, post_pk: postPk }),
        }),

    overview: (username, accountId, dateFrom, dateTo, bucket = 'week') =>
        jsonFetch(`${API}/api/social/metrics/overview?${qs({
            username, account_id: accountId, date_from: dateFrom, date_to: dateTo, bucket,
        })}`),

    posts: (username, accountId, dateFrom, dateTo) =>
        jsonFetch(`${API}/api/social/metrics/posts?${qs({
            username, account_id: accountId, date_from: dateFrom, date_to: dateTo,
        })}`),

    sentimentFlow: (username, accountId, dateFrom, dateTo, bucket = 'week') =>
        jsonFetch(`${API}/api/social/metrics/sentiment-flow?${qs({
            username, account_id: accountId, date_from: dateFrom, date_to: dateTo, bucket,
        })}`),

    postDetail: (username, postPk) =>
        jsonFetch(`${API}/api/social/posts/detail?${qs({ username, post_pk: postPk })}`),

    themeComments: (username, accountId, theme, dateFrom, dateTo) =>
        jsonFetch(`${API}/api/social/metrics/theme-comments?${qs({
            username, account_id: accountId, theme, date_from: dateFrom, date_to: dateTo,
        })}`),

    featuredPosts: (username, accountId) =>
        jsonFetch(`${API}/api/social/metrics/featured?${qs({ username, account_id: accountId })}`),

    setFeatured: (username, postPk, featured) =>
        jsonFetch(`${API}/api/social/posts/featured`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, post_pk: postPk, featured }),
        }),

    hidePost: (username, postPk, hidden) =>
        jsonFetch(`${API}/api/social/posts/hide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, post_pk: postPk, hidden }),
        }),
};

// Cliente del módulo de Monitoreo de Medios por keyword (noticias + TikTok),
// usado por el Dashboard General para el Share of Voice competitivo.
export const mediaApi = {
    // keywords + su clasificación marca/competidor (kinds: {keyword: 'own'|'interest'})
    keywords: (username) =>
        jsonFetch(`${API}/api/media-keywords?${qs({ username })}`),

    // clasifica una keyword: kind '' des-clasifica
    setKeywordKind: (username, keyword, kind) =>
        jsonFetch(`${API}/api/media-keywords/kind?${qs({ username })}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ keyword, kind }),
        }),

    // feed de noticias cacheado (artículos con queryIndex -> keyword)
    news: (username) =>
        jsonFetch(`${API}/api/media-news?${qs({ username })}`),

    // último snapshot de TikTok (posts con .keyword)
    tiktok: (username) =>
        jsonFetch(`${API}/api/media-social?${qs({ username, platform: 'tiktok' })}`),
};
